/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 22/04/22
 */

import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { useGetRequest } from '@/utils/useStatefulRequests';
import React, { useLayoutEffect } from 'react';
import { useAsyncLayoutEffect } from '@/utils/hooks/useAsyncEffect';
import { LoadingSpinner } from '@/components';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { LabeledSelect } from '@/components/FormInputField';
import { Organisation } from '@/wpt-lib/interfaces/Organisation';

interface OrganisationPickerProps {
    assignment: Assignment;
    organisationId: number | undefined;
    facultyId: number;
    setOrganisationId(id: number | undefined): void;
    isDisabled?: boolean;
    label?: string;
    all?: boolean;
    showInactive?: boolean;
}

const OrganisationPicker = ({
    organisationId,
    facultyId,
    assignment,
    setOrganisationId,
    label = 'Organisation',
    all,
    showInactive,
}: OrganisationPickerProps) => {
    const { error, loading, responseData: organisations, makeRequest } = useGetRequest<Organisation[]>();

    useLayoutEffect(() => {
        setOrganisationId(undefined);
    }, [facultyId]);

    useAsyncLayoutEffect(async () => {
        try {
            await makeRequest(`faculty/${facultyId}/organisation`, {
                params: {
                    assignment: assignment.id,
                    all: showInactive ? true : undefined,
                },
            });
        } catch (e) {
            // handled by hook
        }
    }, [facultyId, showInactive]);

    useLayoutEffect(() => {
        if (organisations?.length) {
            organisations.sort((a, b) => a.name.localeCompare(b.name));
            if (!all) setOrganisationId(organisations[0].id);
        }
    }, [organisations]);

    if (loading) {
        return <LoadingSpinner />;
    }
    if (error || !organisations) {
        return <SimpleAlert variant="subtle">Could not load organisation units.</SimpleAlert>;
    }

    return (
        <LabeledSelect
            label={label + ':'}
            labelProps={{ fontWeight: 'bold' }}
            onChange={(ev) => setOrganisationId(ev.target.value !== 'all' ? parseInt(ev.target.value) : undefined)}
            value={organisationId}
            minW="140px"
            width="fit-content"
            controlProps={{ width: 'inherit' }}
        >
            {all && <option value="all">All</option>}
            {organisations
                .filter((o) => showInactive || !o.inactive)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(({ id, name }) => (
                    <option value={`${id}`} key={id}>
                        {name}
                    </option>
                ))}
        </LabeledSelect>
    );
};

export default OrganisationPicker;
